import React from "react";
import * as Icons from "@wix/wix-ui-icons-common";
import {
  Box,
  Card,
  Button,
  Text,
  Page,
  Layout,
  Cell,
  Breadcrumbs,
  Timeline,
  Badge,
} from "@wix/design-system";
import "@wix/design-system/styles.global.css";
import { TrademarkRequest } from "./TrademarkRequestsList";

interface ViewTrademarkRequestProps {
  request: TrademarkRequest;
  onEdit: () => void;
  onBack: () => void;
  onRevertToDraft: () => void;
}

const ViewTrademarkRequest: React.FC<ViewTrademarkRequestProps> = ({
  request,
  onEdit,
  onBack,
  onRevertToDraft,
}) => {
  const isDraft = request.status === "draft";

  // Format date for timeline suffix
  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { 
      year: "numeric", 
      month: "short", 
      day: "numeric" 
    });
  };

  // Build timeline items from request history
  const buildTimelineItems = () => {
    const items: any[] = [];

    // Current status item
    items.push({
      label: (
        <Box gap="2">
          <Text size="small">Request status updated</Text>
          <Badge
            skin={request.status === "draft" ? "neutral" : request.status === "submitted" ? "standard" : request.status === "in-review" ? "warning" : request.status === "approved" ? "success" : "danger"}
            size="small"
          >
            {request.status === "draft" ? "Draft" : request.status === "submitted" ? "Submitted" : request.status === "in-review" ? "In Review" : request.status === "approved" ? "Approved" : "Rejected"}
          </Badge>
        </Box>
      ),
      suffix: formatDate(request.updatedAt) || formatDate(request.createdAt),
    });

    // Submitted item
    if (request.submittedAt && request.status !== "draft") {
      items.push({
        label: "Request submitted",
        suffix: formatDate(request.submittedAt),
      });
    }

    // Created item
    if (request.createdAt) {
      items.push({
        label: "Trademark request created",
        suffix: formatDate(request.createdAt),
      });
    }

    return items;
  };

  const [timelineItems, setTimelineItems] = React.useState(buildTimelineItems());

  return (
    <Page height="100vh">
      <Page.Header
        title={request.trademarkName}
        subtitle="View trademark request details"
        actionsBar={
          <Box gap="SP2">
            {isDraft && (
              <Button prefixIcon={<Icons.Edit />} onClick={onEdit}>
                Edit
              </Button>
            )}
            {!isDraft && (
              <Button 
                onClick={onRevertToDraft}
                skin="inverted"
              >
                Revert to Draft
              </Button>
            )}
            <Button skin="inverted" onClick={onBack}>
              Back
            </Button>
          </Box>
        }
        breadcrumbs={
          <Breadcrumbs
            activeId="2"
            items={[
              { id: "1", value: "Trademark Requests" },
              { id: "2", value: request.trademarkName },
            ]}
            onClick={() => {}}
          />
        }
        showBackButton
        onBackClicked={onBack}
      />
      <Page.Content>
        <Layout>
          <Cell span={8}>
            <Layout>
              <Cell>
                <Card>
                  <Card.Header
                    title="Trademark Information"
                    subtitle="Details about the trademark"
                  />
                  <Card.Divider />
                  <Card.Content>
                    <Box direction="vertical" gap="SP4">
                      <Box direction="vertical" gap="SP1">
                        <Text size="small" weight="bold" secondary>
                          Trademark Name
                        </Text>
                        <Text>{request.trademarkName}</Text>
                      </Box>
                      <Box direction="vertical" gap="SP1">
                        <Text size="small" weight="bold" secondary>
                          Description
                        </Text>
                        <Text>{request.description || "No description provided"}</Text>
                      </Box>
                      <Box direction="vertical" gap="SP1">
                        <Text size="small" weight="bold" secondary>
                          Goods and Services
                        </Text>
                        <Text>{request.goodsAndServices || "No goods and services specified"}</Text>
                      </Box>
                    </Box>
                  </Card.Content>
                </Card>
              </Cell>
              <Cell>
                <Card>
                  <Card.Header
                    title="Applicant Information"
                    subtitle="Contact and address details"
                  />
                  <Card.Divider />
                  <Card.Content>
                    <Box direction="vertical" gap="SP4">
                      <Box direction="vertical" gap="SP1">
                        <Text size="small" weight="bold" secondary>
                          Name
                        </Text>
                        <Text>{request.applicantName}</Text>
                      </Box>
                      <Box direction="vertical" gap="SP1">
                        <Text size="small" weight="bold" secondary>
                          Email
                        </Text>
                        <Text>{request.applicantEmail}</Text>
                      </Box>
                      <Box direction="vertical" gap="SP1">
                        <Text size="small" weight="bold" secondary>
                          Address
                        </Text>
                        <Text>
                          {request.applicantAddress}
                          <br />
                          {request.applicantCity}, {request.applicantState}{" "}
                          {request.applicantZipCode}
                          <br />
                          {request.applicantCountry}
                        </Text>
                      </Box>
                    </Box>
                  </Card.Content>
                </Card>
              </Cell>
            </Layout>
          </Cell>
          <Cell span={4}>
            <Page.Sticky>
              <Card>
                <Card.Header
                  title="Request Activity"
                  subtitle="Status and timeline"
                />
                <Card.Divider />
                <Card.Content>
                  <Timeline
                    items={[
                      ...timelineItems,
                    ]}
                  />
                </Card.Content>
              </Card>
            </Page.Sticky>
          </Cell>
        </Layout>
      </Page.Content>
    </Page>
  );
};

export default ViewTrademarkRequest;

